import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { getUser, isAuthenticated, logout } from "../../utils/auth";
import logo from "../../images/greenlam-logo.png";
import { authAPI, notificationAPI } from "../../services/api";
import "./Header.css";

const NOTIF_PAGE_SIZE = 5;

const Header = ({ onMenuClick, sidebarOpen = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const user = isAuthenticated() ? getUser() : null;
  const [notifCount, setNotifCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifLoadingMore, setNotifLoadingMore] = useState(false);
  const [notifLogs, setNotifLogs] = useState([]);
  const [notifPage, setNotifPage] = useState(1);
  const [notifHasMore, setNotifHasMore] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const notificationRef = useRef(null);
  const listContainerRef = useRef(null);
  const loadInFlightRef = useRef(false);
  const markedReadRef = useRef(false);

  const userParams = useMemo(() => {
    const u = user || {};
    const recipientUserIdCode = (
      u.UserIdCode ||
      u.userIdCode ||
      u.UserIDCode ||
      u.userIDCode ||
      ""
    )
      .toString()
      .trim();
    const recipientEmail = (
      u.Email ||
      u.email ||
      u.EmailId ||
      u.emailId ||
      u.EmailID ||
      u.emailID ||
      ""
    )
      .toString()
      .trim();
    return { recipientUserIdCode, recipientEmail };
  }, [user]);

  const canFetchNotifications =
    !!userParams.recipientUserIdCode || !!userParams.recipientEmail;

  const stripHtml = (html) => {
    if (!html) return "";
    return html
      .toString()
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<\/p>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const getNotificationPreview = (notification) => {
    const body = (notification?.Body || notification?.body || "").toString();
    const subject = (notification?.Subject || notification?.subject || "").toString();
    const plainBody = stripHtml(body);
    const text = plainBody || subject || "Notification";
    return text.length > 140 ? `${text.slice(0, 140)}…` : text;
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/dashboard") return "Dashboard";
    if (path === "/raise-complaint") return "RAISE SR";
    if (path === "/raise-complaint-checklist") return "RAISE SR";
    if (path === "/complaints") return "View SR";
    if (path === "/users") return "User Management";
    return "CSRP";
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const refreshCount = useCallback(async () => {
    if (!canFetchNotifications) return;
    try {
      const res = await notificationAPI.getUserCount(userParams);
      const count = res?.data ?? res?.Data ?? 0;
      setNotifCount(Number.isFinite(Number(count)) ? Number(count) : 0);
    } catch (e) {
      setNotifCount(0);
    }
  }, [canFetchNotifications, userParams]);

  const loadLogsPage = useCallback(
    async (nextPage, reset = false) => {
      if (!canFetchNotifications) return;
      if (loadInFlightRef.current) return;
      loadInFlightRef.current = true;

      if (reset) {
        setNotifLogs((prev) => {
          if (prev.length === 0) setNotifLoading(true);
          return prev;
        });
      } else {
        setNotifLoadingMore(true);
      }

      try {
        const res = await notificationAPI.getUserLogs({
          ...userParams,
          pageNumber: nextPage,
          pageSize: NOTIF_PAGE_SIZE,
        });
        const payload = res?.data ?? res?.Data ?? {};
        const logs = payload?.Logs || payload?.logs || [];
        const items = Array.isArray(logs) ? logs : [];
        const totalRecords = Number(
          payload?.TotalRecords ?? payload?.totalRecords ?? 0
        );

        setNotifLogs((prev) => (reset ? items : [...prev, ...items]));
        const loaded = (nextPage - 1) * NOTIF_PAGE_SIZE + items.length;
        setNotifHasMore(loaded < totalRecords && items.length > 0);
        setNotifPage(nextPage);
      } catch (e) {
        if (reset) setNotifLogs([]);
        setNotifHasMore(false);
      } finally {
        setNotifLoading(false);
        setNotifLoadingMore(false);
        loadInFlightRef.current = false;
      }
    },
    [canFetchNotifications, userParams]
  );

  const markReadInBackground = useCallback(() => {
    if (!canFetchNotifications || markedReadRef.current) return;
    markedReadRef.current = true;
    void notificationAPI
      .markReadAll(userParams)
      .then(() => refreshCount())
      .catch(() => {
        markedReadRef.current = false;
      });
  }, [canFetchNotifications, userParams, refreshCount]);

  const deriveParentSrFromChild = (childSrCode) => {
    const raw = (childSrCode || "").toString().trim();
    if (!raw) return "";

    const m = raw.match(/^(.+?)\/DP\/([^\/-]+)(?:-\d+)?$/i);
    if (m && m[1] && m[2]) return `${m[1]}/${m[2]}`;

    if (raw.toUpperCase().includes("/DP/")) {
      return raw.replace(/\/DP\//i, "/").replace(/-\d+$/, "");
    }

    return raw.replace(/-\d+$/, "");
  };

  const handleNotificationItemClick = (notification) => {
    const parent =
      (notification?.ParentSRCode || notification?.parentSRCode || "")
        .toString()
        .trim() ||
      deriveParentSrFromChild(
        notification?.ChildSRCode || notification?.childSRCode || ""
      );

    const child = (notification?.ChildSRCode || notification?.childSRCode || "")
      .toString()
      .trim();

    if (!parent) return;

    const path = `/complaints/${encodeURIComponent(parent)}`;
    const qs = child ? `?childSrCode=${encodeURIComponent(child)}` : "";

    setNotificationOpen(false);
    navigate(`${path}${qs}`);
  };

  const handleNotificationClick = () => {
    const next = !notificationOpen;
    setNotificationOpen(next);
    if (!next) return;

    // Open immediately; load progressively like SR activity log (non-blocking).
    if (notifLogs.length === 0) {
      void loadLogsPage(1, true);
    }
    markReadInBackground();
  };

  const handleShowAllNotifications = () => {
    if (notifLoadingMore || !notifHasMore) return;
    void loadLogsPage(notifPage + 1, false);
  };

  const handleNotificationScroll = () => {
    const el = listContainerRef.current;
    if (!el || notifLoadingMore || !notifHasMore || loadInFlightRef.current)
      return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (nearBottom) {
      void loadLogsPage(notifPage + 1, false);
    }
  };

  // Prefetch badge + first page so bell open feels instant.
  useEffect(() => {
    markedReadRef.current = false;
    void refreshCount();
    if (canFetchNotifications) {
      void loadLogsPage(1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    userParams.recipientUserIdCode,
    userParams.recipientEmail,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target) &&
        !event.target.closest('[aria-label="notifications"]')
      ) {
        setNotificationOpen(false);
      }
    };

    if (notificationOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [notificationOpen]);

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: "#424242",
        boxShadow: "none",
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ minHeight: "64px !important" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: sidebarOpen ? "250px" : "64px",
            justifyContent: "flex-start",
            transition: "width 0.3s",
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Greenlam Industries Limited"
            sx={{
              height: sidebarOpen ? "40px" : "30px",
              width: "auto",
              objectFit: "contain",
              transition: "height 0.3s",
            }}
          />
        </Box>

        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={onMenuClick}
          sx={{ mr: 2 }}
        >
          {sidebarOpen ? <MenuIcon /> : <ArrowForwardIcon />}
        </IconButton>

        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 1,
            color: "#ffffff",
            fontWeight: 500,
            fontSize: "18px",
          }}
        >
          {getPageTitle()}
        </Typography>

        <Box sx={{ position: "relative", mr: 1 }}>
          <IconButton
            color="inherit"
            aria-label="notifications"
            onClick={handleNotificationClick}
            sx={{ color: "#ffffff", position: "relative" }}
            className="notification-icon-btn"
          >
            <NotificationsIcon />
            {notifCount > 0 && (
              <span className="notification-badge">
                {notifCount > 9 ? "9+" : notifCount}
              </span>
            )}
          </IconButton>

          {notificationOpen && (
            <div className="notification-dropdown" ref={notificationRef}>
              <div className="notification-dropdown-header">
                <h6 className="notification-dropdown-title">Notifications</h6>
              </div>

              <div
                className="notification-list-container"
                ref={listContainerRef}
                onScroll={handleNotificationScroll}
              >
                {notifLoading && notifLogs.length === 0 ? (
                  <div className="notification-empty">Loading...</div>
                ) : notifLogs.length === 0 ? (
                  <div className="notification-empty">
                    No notifications found.
                  </div>
                ) : (
                  <>
                    <ul className="notification-list">
                      {notifLogs.map((n) => {
                        const id =
                          n?.NotificationLogId ||
                          n?.notificationLogId ||
                          `${n?.ParentSRCode || ""}-${n?.ChildSRCode || ""}`;

                        const subject = (
                          n?.Subject ||
                          n?.subject ||
                          ""
                        ).toString();
                        const preview = getNotificationPreview(n);
                        const sr = (
                          n?.ChildSRCode ||
                          n?.childSRCode ||
                          n?.ParentSRCode ||
                          n?.parentSRCode ||
                          ""
                        )
                          .toString()
                          .trim();
                        const sentOn =
                          n?.SentOn ||
                          n?.sentOn ||
                          n?.CreatedOn ||
                          n?.createdOn;
                        const sentText = sentOn
                          ? new Date(sentOn).toLocaleString()
                          : "";

                        const isReadRaw = n?.IsRead ?? n?.isRead ?? null;
                        const unread =
                          isReadRaw === 0 ||
                          isReadRaw === false ||
                          isReadRaw === "0";

                        return (
                          <li
                            key={id}
                            className={`notification-item ${
                              unread ? "unread" : ""
                            }`}
                            onClick={() => handleNotificationItemClick(n)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleNotificationItemClick(n);
                              }
                            }}
                          >
                            <div className="notification-icon-wrapper gray">
                              <NotificationsIcon className="notification-icon" />
                              {unread && (
                                <span className="notification-unread-badge"></span>
                              )}
                            </div>
                            <div className="notification-content">
                              {sr ? (
                                <p className="notification-sr" title={sr}>
                                  <span className="notification-sr-label">
                                    SR:
                                  </span>{" "}
                                  <span className="notification-sr-code">
                                    {sr}
                                  </span>
                                </p>
                              ) : (
                                <p
                                  className={`notification-message ${
                                    unread ? "unread" : ""
                                  }`}
                                  title={subject || ""}
                                >
                                  {subject || "Notification"}
                                </p>
                              )}
                              {preview ? (
                                <p
                                  className="notification-body"
                                  title={preview}
                                >
                                  {preview}
                                </p>
                              ) : null}
                              <p className="notification-timestamp">
                                {sentText}
                              </p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    {notifLoadingMore ? (
                      <div className="notification-loading-more">
                        Loading more...
                      </div>
                    ) : null}
                  </>
                )}
              </div>

              <div className="notification-dropdown-footer">
                <button
                  className="notification-show-all-btn w-100"
                  onClick={handleShowAllNotifications}
                  type="button"
                  disabled={notifLoadingMore || !notifHasMore}
                >
                  {notifHasMore
                    ? notifLoadingMore
                      ? "Loading..."
                      : "Show all"
                    : "No more notifications"}
                </button>
              </div>
            </div>
          )}
        </Box>

        <Box
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          onClick={handleMenuClick}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: "#1976d2", mr: 1 }}>
            <AccountCircleIcon />
          </Avatar>
          <Typography variant="body2" sx={{ color: "#ffffff", mr: 0.5 }}>
            {user?.displayName || "Admin"}
          </Typography>
          <ArrowDropDownIcon sx={{ color: "#ffffff" }} />
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              minWidth: "200px",
            },
          }}
        >
          <MenuItem
            component="a"
            href={`${process.env.PUBLIC_URL || ""}/User_Guide/${encodeURIComponent(
              "CSRP 2.0 User Guide.html"
            )}`}
            download="CSRP 2.0 User Guide.html"
            onClick={handleMenuClose}
          >
            Download User Guide
          </MenuItem>
          <MenuItem
            onClick={async () => {
              handleMenuClose();
              try {
                await authAPI.logout();
              } catch {
                // cookie may already be gone
              }
              logout();
            }}
          >
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
